import { useState } from "react";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { api, errText } from "@/lib/api";

export default function ResetPassword() {
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) return setErr("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      await api.post("/auth/logout").catch(() => {});
      setDone(true);
    } catch (x) { setErr(errText(x)); }
    setBusy(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="fade-up w-full max-w-sm space-y-5" data-testid="reset-password-screen">
        <img src="/logo.png" alt="Indo Scots Global School" className="h-14 w-auto" />
        {done ? (
          <div className="space-y-4" data-testid="reset-success">
            <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-plum-950"><CheckCircle2 className="h-6 w-6 text-emerald-600" />Password updated</h2>
            <p className="text-sm text-slate-600">You've been signed out on all devices. Sign in with your new password.</p>
            <Button onClick={() => window.location.assign("/")} className="w-full bg-plum-700 hover:bg-plum-800" data-testid="reset-goto-login-btn">Go to sign in</Button>
          </div>
        ) : !token ? (
          <div className="space-y-3" data-testid="reset-missing-token">
            <h2 className="font-display text-2xl font-bold text-plum-950">Invalid reset link</h2>
            <p className="text-sm text-slate-600">Please open the link from your email, or request a new one from the sign-in page.</p>
            <Button onClick={() => window.location.assign("/")} variant="outline" className="w-full">Back to sign in</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
            <div>
              <h2 className="font-display text-2xl font-bold text-plum-950">Set a new password</h2>
              <p className="mt-1 text-sm text-slate-500">At least 8 characters. You'll be signed out everywhere after saving.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="npw" className="text-xs text-slate-600">New password</Label>
              <PasswordInput id="npw" autoComplete="new-password" minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} required className="bg-white" data-testid="reset-new-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="npw2" className="text-xs text-slate-600">Confirm new password</Label>
              <PasswordInput id="npw2" autoComplete="new-password" minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} required className="bg-white" data-testid="reset-confirm-password-input" />
            </div>
            {err && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="reset-error">{err}</p>}
            <Button type="submit" disabled={busy} className="w-full bg-plum-700 hover:bg-plum-800" data-testid="reset-submit-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}Update password
            </Button>
            <a href="/" className="block text-sm font-medium text-plum-700 hover:underline">Back to sign in</a>
          </form>
        )}
      </div>
    </div>
  );
}
